import {
  fetchConvertToAssets,
  getWithdrawRequestDetails,
  withdrawFromPool,
} from "@/services/staking";
import { useTrackUserEvent } from "@/utils/analytics";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { useCallback, useEffect, useState } from "react";
import { useActiveAccount } from "thirdweb/react";
import ConnectWalletButton from "../ConnectWalletButton";
import CooldownPeriod from "../CooldownPeriod";
import Button from "../SecondaryButton";
// import { getWithdrawalRequestsForAddress } from "@/services/subgraph";
import Skeleton from "react-loading-skeleton";
import WithdrawSuccessfulModal from "../Modals/WithdrawSuccessfulModal";
import ErrorModal from "../Modals/ErrorModal";
import { formatEther } from "ethers";
import { NotificationType, showToastNotification } from "@/utils/notification";
import { networkMap, networkType, NetworkType } from "@/config";

dayjs.extend(duration);

const Withdraw = ({
  switchTab,
}: {
  switchTab: (tab: "stake" | "unstake") => void;
}) => {
  const trackUserEvent = useTrackUserEvent();
  const activeAccount = useActiveAccount();

  const [withdrawStatus, setWithdrawStatus] = useState<
    "" | "withdrawing" | "success" | "error"
  >("");
  const [isLoading, setIsLoading] = useState(true);
  const [withdrawRequest, setWithdrawRequest] = useState<{
    shares: number;
    unlockTime: number;
  } | null>(null);

  // const [subgraphData, setSubgraphData] = useState<Awaited<
  //   ReturnType<typeof getWithdrawalRequestsForAddress>
  // > | null>(null);

  const fetchBalance = useCallback(
    async (isRefresh?: boolean) => {
      if (activeAccount) {
        if (!isRefresh) setIsLoading(true);
        try {
          const [withdrawRequest] = await Promise.allSettled([
            getWithdrawRequestDetails(activeAccount),
            // getWithdrawalRequestsForAddress(activeAccount.address),
          ]);

          // if (subgraphData.status === "fulfilled") {
          //   setSubgraphData(subgraphData.value);
          // } else {
          //   setSubgraphData(null);
          // }
          if (withdrawRequest.status === "fulfilled") {
            const convertedUnstakedAMount = await fetchConvertToAssets(
              BigInt(withdrawRequest.value[0])
            );
            setWithdrawRequest({
              shares: Number(formatEther(convertedUnstakedAMount)),
              unlockTime: Number(withdrawRequest.value[1]),
            });
          } else {
            setWithdrawRequest(null);
          }
        } catch (e) {
          console.log("error while fetching withdraw details: ", e);
        }
        if (!isRefresh) setIsLoading(false);
      }
    },
    [activeAccount]
  );

  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  let startTime = dayjs();
  let endTime = dayjs();
  let isInCooldown = false;
  let remainingTime = dayjs.duration({ minutes: 0 });
  if (withdrawRequest) {
    const totalDuration = dayjs.duration({ days: 7 });
    endTime = dayjs(withdrawRequest?.unlockTime * 1000);
    startTime = endTime.subtract(totalDuration);
    remainingTime = dayjs.duration(endTime.diff(dayjs()));

    isInCooldown = endTime.isAfter(dayjs());
  }

  if (remainingTime.asMilliseconds() <= 0) {
    fetchBalance(true);
  }

  const { blockExplorerUrls, cooldownDays } =
    networkMap[networkType as NetworkType];

  const withdraw = async () => {
    if (activeAccount) {
      try {
        setWithdrawStatus("withdrawing");
        const txnHash = await withdrawFromPool(activeAccount);
        fetchBalance();
        setWithdrawStatus("success");
        showToastNotification({
          title: "Withdrawal Successful",
          message: "",
          txnLink: `${blockExplorerUrls}/tx/${txnHash}`,
          timestamp: new Date(),
          type: NotificationType.Success,
        });
      } catch (e) {
        console.log("Error during withdrawal: ", e);
        setWithdrawStatus("error");
        showToastNotification({
          title: "Your withdraw request couldn't be processed.",
          message: "Please try again or contact support if the issue persists.",
          timestamp: new Date(),
          type: NotificationType.Error,
        });
      }

      await new Promise((resolve) => setTimeout(resolve, 2000));

      const event = new CustomEvent("refreshBalance");
      document?.dispatchEvent(event);
      const event2 = new CustomEvent("refreshStats");
      document?.dispatchEvent(event2);
    }
  };

  return (
    <div className="flex flex-col h-full justify-between font-inter text-foreground">
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-2 py-4">
          <span className="text-sm">Withdrawable Amount</span>
          {!!activeAccount?.address &&
            (isLoading ? (
              <Skeleton height="32px" width="100px" />
            ) : (
              <span className="text-2xl leading-8">
                {isInCooldown ? 0 : withdrawRequest?.shares || 0} SPON
              </span>
            ))}
          {!activeAccount?.address && (
            <span className="text-2xl leading-8">N/A SPON</span>
          )}
        </div>
        {activeAccount?.address && withdrawRequest && isInCooldown && (
          <CooldownPeriod
            d={remainingTime.days()}
            h={remainingTime.hours()}
            m={remainingTime.minutes()}
            totalDays={cooldownDays}
            start={
              withdrawRequest.unlockTime
                ? dayjs(startTime).format("MMM DD, YYYY")
                : ""
            }
            end={
              withdrawRequest.unlockTime
                ? dayjs(endTime).format("MMM DD, YYYY")
                : ""
            }
            unstakingAmount={withdrawRequest.shares}
            onRefresh={async () => {
              await fetchBalance(true);
            }}
          />
        )}
      </div>
      <div className="flex flex-col gap-[18px] mt-12">
        {activeAccount?.address ? (
          <>
            <Button
              onClick={() => {
                withdraw();
                trackUserEvent("Withdraw Tab- Withdraw Click");
              }}
              disabled={
                isInCooldown ||
                withdrawStatus === "withdrawing" ||
                isLoading ||
                withdrawRequest?.shares === 0
              }
              variant="light"
              loading={withdrawStatus === "withdrawing"}
            >
              WITHDRAW
            </Button>
            {/* {!!subgraphData?.withdrawRequests.at(-1)?.transactionHash && (
              <Button
                onClick={() => {
                  trackUserEvent("Withdraw Tab- View Transaction Click");
                  if (subgraphData?.withdrawRequests.at(-1)?.transactionHash)
                    window.open(
                      `https://sepolia.basescan.org/tx/${
                        subgraphData?.withdrawRequests.at(-1)?.transactionHash
                      }`,
                      "_blank"
                    );
                }}
                variant="dark"
                disabled={
                  !subgraphData?.withdrawRequests.at(-1)?.transactionHash ||
                  isLoading
                }
              >
                VIEW TRANSACTION
              </Button>
            )} */}
          </>
        ) : (
          <ConnectWalletButton />
        )}
      </div>
      <WithdrawSuccessfulModal
        close={() => setWithdrawStatus("")}
        isOpen={withdrawStatus === "success"}
        onStakeMoreClick={() => {
          switchTab("stake");
        }}
      />
      <ErrorModal
        close={() => setWithdrawStatus("")}
        isOpen={withdrawStatus === "error"}
        errorTitle="Withdrawal Failed"
        errorDescription="Something went wrong while processing your withdrawal request."
        handleTryAgain={() => {
          trackUserEvent("Withdraw Error Modal- Try Again Click");
          withdraw();
        }}
      />
    </div>
  );
};

export default Withdraw;
